import { Dtos } from "@inrev/common";
import { Option } from "@inrev/core";
import {
	canRelate,
	type ProCon,
	proConsQueryKey,
	relationshipVerb,
	usePendingIds,
} from "@portal/api/decisioning";
import { useCreateProConRelationship } from "@portal/api/decisioning/admin";
import { useMemo, useState } from "react";
import { SearchableDropdownInput } from "../../../../form/inputs/SearchableDropdownInput";
import { TextAreaInput } from "../../../../form/inputs/TextAreaInput";
import { PanelSheet } from "../../../../patterns/panel-sheet";
import { SegmentedControls, Tag, Text } from "../../../../primitives";
import { PRO_CON_IMPORTANCE_LABELS } from "../../decisioningLabels";
import { CloseWhenMissing } from "../CloseWhenMissing";
import { useDecisioningData } from "../DecisioningDataContext";
import { type DecisioningSheet, useDecisioningPanel } from "../DecisioningPanelContext";
import { PanelField } from "../PanelField";
import { ImportanceMeter } from "./ImportanceMeter";
import { formatObservedValue } from "./observedValue";

type RelationshipType = Dtos.Decisioning.ProConRelationshipType;

const TYPES = Dtos.Decisioning.proConRelationshipTypeSchema.options;
const TYPE_LABELS: Record<RelationshipType, string> = {
	mitigates: "Mitigates",
	compounds: "Compounds",
	explains: "Explains",
};
const KIND_GROUPS: Record<ProCon["kind"], string> = { pro: "Pros", con: "Cons" };

const entrySummary = (proCon: ProCon) =>
	Option.filterNone([
		Option.map(proCon.observedValue, (value) =>
			formatObservedValue(value, proCon.factor.valueType),
		),
		proCon.detail,
	]).join(" · ");

/** The board's importance meter. The meter is hidden from screen readers, so the label stands in. */
const EntryImportance = ({ proCon }: { proCon: ProCon }) => {
	const label = PRO_CON_IMPORTANCE_LABELS[proCon.importance];
	return (
		<span title={label}>
			<ImportanceMeter weight={proCon.importance} side={proCon.kind} />
			<span className="sr-only">{label}</span>
		</span>
	);
};

/** Connects the card's factor to another with a relationship the type allows. */
export const ConnectFactorSheet = ({
	sheet,
}: {
	sheet: Extract<DecisioningSheet, { kind: "connect" }>;
}) => {
	const { proConsById } = useDecisioningData();
	const source = proConsById.get(sheet.proConId);
	return source ? <ConnectFactorForm source={source} /> : <CloseWhenMissing />;
};

const ConnectFactorForm = ({ source }: { source: ProCon }) => {
	const { accountId, closeSheet, jumpTo, toastOnSave } = useDecisioningPanel();
	const { proCons, relationships, proConsById } = useDecisioningData();
	const { createRelationshipAsync } = useCreateProConRelationship(accountId);
	const pendingProConIds = usePendingIds(proConsQueryKey(accountId));
	const [type, setType] = useState<RelationshipType>("mitigates");
	const [targetId, setTargetId] = useState("");
	const [rationale, setRationale] = useState("");

	const connected = useMemo(
		() =>
			new Set(
				relationships.flatMap((relationship) => {
					if (relationship.sourceProConId === source.id) return [relationship.targetProConId];
					if (relationship.targetProConId === source.id) return [relationship.sourceProConId];
					return [];
				}),
			),
		[relationships, source.id],
	);
	const candidates = useMemo(
		() =>
			proCons.filter(
				(candidate) =>
					candidate.id !== source.id &&
					!pendingProConIds.has(candidate.id) &&
					!connected.has(candidate.id) &&
					canRelate(type, source, candidate),
			),
		[proCons, source, connected, pendingProConIds, type],
	);
	const options = useMemo(
		() =>
			candidates.map((candidate) => ({
				value: candidate.id,
				label: candidate.factor.name,
				group: KIND_GROUPS[candidate.kind],
				description: entrySummary(candidate),
				leading: <EntryImportance proCon={candidate} />,
				searchValues: candidate.detail ? [candidate.detail] : undefined,
			})),
		[candidates],
	);

	const dirty = targetId !== "" || rationale !== "" || type !== "mitigates";
	const target = candidates.find((candidate) => candidate.id === targetId);
	const trimmedRationale = rationale.trim();
	const missingTarget = target === undefined;
	const missingRationale = !missingTarget && trimmedRationale === "";
	const blocked = missingTarget || missingRationale;

	const status = (() => {
		if (missingTarget) return "Choose a factor to connect.";
		if (missingRationale) return "Add a rationale for the connection.";
		return `${source.factor.name} ${relationshipVerb({ type, sourceProConId: source.id }, source.id)} ${target.factor.name}.`;
	})();

	const handleConnect = () => {
		if (!target || blocked) return;
		const saved = createRelationshipAsync({
			sourceProConId: source.id,
			targetProConId: target.id,
			type,
			rationale: trimmedRationale,
		});
		closeSheet();
		jumpTo({ tab: "factors", id: source.id });
		toastOnSave(
			saved,
			`Connected ${source.factor.name} to ${proConsById.get(target.id)?.factor.name}`,
		);
	};

	return (
		<PanelSheet
			title="Connect factor"
			status={status}
			statusTone={blocked ? "danger" : "default"}
			onCancel={closeSheet}
			dirty={dirty}
			primaryLabel="Connect"
			onPrimary={handleConnect}
			primaryDisabled={blocked}
			testId="connect-factor-sheet"
		>
			<div
				className="flex flex-wrap items-center gap-2 rounded-lg bg-surface-light p-3"
				data-testid="connect-factor-source"
			>
				<Tag color={source.kind === "pro" ? "green" : "red"}>
					{source.kind === "pro" ? "Pro" : "Con"}
				</Tag>
				<Text type="body-strong" color="primary">
					{source.factor.name}
				</Text>
			</div>
			<PanelField label="Relationship" required>
				{({ id }) => (
					<SegmentedControls<RelationshipType>
						id={id}
						aria-label="Relationship"
						value={type}
						onChange={setType}
						className="self-start"
					>
						{TYPES.map((value) => (
							<SegmentedControls.Segment key={value} value={value}>
								{TYPE_LABELS[value]}
							</SegmentedControls.Segment>
						))}
					</SegmentedControls>
				)}
			</PanelField>
			<PanelField label="Connects to" required>
				{({ id, labelId }) => (
					<SearchableDropdownInput
						id={id}
						aria-labelledby={labelId}
						compact
						anchor="bottom start"
						value={target?.id ?? ""}
						options={options}
						placeholder="Search factors"
						emptyMessage="No other factors can be connected this way."
						onChange={setTargetId}
					/>
				)}
			</PanelField>
			<PanelField label="Rationale" required>
				{({ id }) => (
					<TextAreaInput
						id={id}
						value={rationale}
						invalid={missingRationale}
						data-testid="connect-factor-rationale"
						className="min-h-[76px]"
						onChange={(event) => setRationale(event.target.value ?? "")}
					/>
				)}
			</PanelField>
		</PanelSheet>
	);
};
